import React, { useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { formatWeightKg } from "../lib/weightUnits";
import { formatIndiaDateTime } from "../lib/indiaTime";
import SettlementStepper from "./SettlementStepper";
import OutwardWeighmentReceiptActions from "./OutwardWeighmentReceiptActions";
import VoucherDocumentActions from "./VoucherDocumentActions";
import TransporterFreightFields, { TransporterSameAsCompanyField } from "./TransporterFreightFields";
import FreightTdsFields from "./FreightTdsFields";
import { applyHaltingPatch, computeHaltingSettlement } from "../lib/freightSettlement";
import DispatchDocsUpload, { DISPATCH_DOC_KINDS } from "./DispatchDocsUpload";
import FinalSettlementWizard from "./FinalSettlementWizard";
import SettlementReviewTable from "./SettlementReviewTable";
import { buildSalesTruckReviewRows } from "../lib/salesSettlementReview";
import {
  CommercialWeightBar,
  GstFields,
  SalesVoucherSummaryRibbon,
  SalesWorkspaceHeader,
  TallyPreviewDialog,
  SalesStageBanners,
  SalesAdjustmentBreakdown,
  PostSettlementJournalButton,
  commercialPayload,
  openSalesTallyPreview,
  seedCommercialForm,
} from "./SalesSettlementChrome";

function moneyInr(v) {
  if (v == null || v === "") return "—";
  return `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function DetailCell({ label, value, emphasize = false }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`mt-0.5 tabular-nums break-words ${emphasize ? "font-semibold text-foreground" : "font-medium"}`}>
        {value ?? "—"}
      </p>
    </div>
  );
}

function qtlLabel(v) {
  if (v == null || v === "") return "—";
  return `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 })} qtl`;
}

/** Full freight calc breakdown for finalized sales trips. */
function FinalizedFreightSettlementDetail({ dispatch, pack, freight }) {
  const ded = freight?.deductions || {};
  const st = dispatch.freight_settlement || ded.freight_settlement || {};
  const slip = ded.freight_slip || pack || {};
  const lines = Array.isArray(st.lines) ? st.lines : (Array.isArray(ded.lines) ? ded.lines : []);
  const cash = Array.isArray(dispatch.freight_cash_collections) && dispatch.freight_cash_collections.length
    ? dispatch.freight_cash_collections
    : (st.cash_collections || ded.cash_collections || []);
  const company = slip.transport_company_name || slip.transport_company_display_name || pack.transport_company_name;
  const transporter = slip.transporter_same_as_company || pack.transporter_same_as_company
    ? (company || slip.transporter_name || pack.transporter_name)
    : (slip.transporter_display_name || slip.transporter_name || slip.transporter_owner_name
      || pack.transporter_display_name || pack.transporter_name);
  const rate = st.freight_rate_per_qtl ?? slip.freight_rate_per_qtl ?? pack.freight_rate_per_qtl;
  const billable = st.freight_billable_qtl;
  const gross = st.gross_freight ?? freight?.gross_amount;
  const net = st.net_freight_payable ?? freight?.net_payable;
  const advance = slip.advance_at_dispatch ?? slip.advance ?? pack.advance_at_dispatch ?? pack.advance;
  const hasShortage = Number(st.shortage_qtl || 0) > 0
    || Number(st.net_shortage_qtl || 0) > 0
    || Number(st.shortage_deduction || 0) > 0;
  const hasHalting = Boolean(st.halting_applicable) || Number(st.halting_charges || 0) > 0;

  return (
    <div className="rounded-md border border-bansal-sage/30 bg-bansal-sage/5 p-2.5 space-y-3" data-testid="finalized-freight-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Freight voucher</p>
          <p className="font-mono text-sm font-semibold">{dispatch.freight_voucher_code}</p>
        </div>
        <p className="text-sm font-semibold tabular-nums">{moneyInr(net)}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <DetailCell label="Transport company" value={company || "—"} />
        <DetailCell label="Transporter" value={transporter || "—"} />
        <DetailCell label="Vehicle" value={slip.vehicle_number || pack.vehicle_number || dispatch.vehicle_number || "—"} />
        <DetailCell label="Bilty no." value={slip.bilty_number || pack.bilty_number || "—"} />
        <DetailCell label="Load date" value={slip.load_date || pack.load_date || "—"} />
        <DetailCell label="Reached" value={slip.reached_date || pack.reached_date || "—"} />
        <DetailCell label="Unload date" value={slip.unload_date || pack.unload_date || "—"} />
        <DetailCell
          label="Route"
          value={[slip.from_place || pack.from_place, slip.to_place || pack.to_place].filter(Boolean).join(" → ") || "—"}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs rounded-md border border-border/50 bg-background/60 p-2">
        <DetailCell label="Billable qtl" value={billable != null ? qtlLabel(billable) : "—"} />
        <DetailCell label="Rate / qtl" value={rate != null ? moneyInr(rate) : "—"} />
        <DetailCell
          label="Load (our WB)"
          value={qtlLabel(st.load_weight_qtl ?? st.mill_net_qtl ?? st.our_net_qtl)}
        />
        <DetailCell
          label="Unload (party WB)"
          value={
            Number(st.unload_weight_qtl ?? st.party_net_qtl) > 0
              ? qtlLabel(st.unload_weight_qtl ?? st.party_net_qtl)
              : "—"
          }
        />
        <DetailCell label="Bags" value={st.load_bags ?? slip.bags ?? dispatch.bags_loaded ?? "—"} />
        <DetailCell label="Gross freight" value={moneyInr(gross)} emphasize />
        {hasHalting ? (
          <DetailCell
            label="Halting"
            value={`${moneyInr(st.halting_charges)}${st.halting_days != null ? ` · ${st.halting_days} day(s)` : ""}`}
          />
        ) : null}
        {advance != null && Number(advance) > 0 ? (
          <DetailCell label="Advance at dispatch" value={moneyInr(advance)} />
        ) : null}
      </div>

      {hasShortage ? (
        <div className="rounded-md border border-orange-200/80 bg-orange-50/50 p-2 space-y-1.5">
          <p className="text-[10px] uppercase tracking-widest text-orange-950/80 font-medium">Shortage</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <DetailCell label="Gross shortage" value={qtlLabel(st.shortage_qtl ?? st.weight_diff_qtl)} />
            <DetailCell
              label="Allowance"
              value={
                st.allowance_qtl != null
                  ? `${qtlLabel(st.allowance_qtl)}${
                    st.shortage_allowance_mode
                      ? ` (${st.shortage_allowance_mode}${st.shortage_allowance_value != null ? ` ${st.shortage_allowance_value}` : ""})`
                      : ""
                  }`
                  : "—"
              }
            />
            <DetailCell label="Net shortage" value={qtlLabel(st.net_shortage_qtl ?? st.weight_short_qtl)} />
            <DetailCell
              label="Deduction"
              value={
                st.shortage_goods_rate_per_qtl
                  ? `${moneyInr(st.shortage_deduction)} @ ${moneyInr(st.shortage_goods_rate_per_qtl)}/qtl`
                  : moneyInr(st.shortage_deduction ?? st.shortage_amount)
              }
            />
          </div>
        </div>
      ) : null}

      <div className="rounded-md border border-border/50 bg-background/60 p-2 space-y-1">
        <div className="flex justify-between gap-2 text-xs">
          <span className="text-muted-foreground">Gross freight</span>
          <span className="tabular-nums font-medium">{moneyInr(gross)}</span>
        </div>
        {lines.length ? (
          lines.map((ln, i) => (
            <div key={`${ln.code || "line"}-${i}`} className="flex justify-between gap-2 text-xs">
              <span className="text-muted-foreground min-w-0 truncate" title={ln.label || ln.code}>
                − {ln.label || ln.code || "Deduction"}
              </span>
              <span className="tabular-nums shrink-0">{moneyInr(ln.amount)}</span>
            </div>
          ))
        ) : (
          <>
            {Number(advance) > 0 ? (
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">− Advance at dispatch</span>
                <span className="tabular-nums">{moneyInr(advance)}</span>
              </div>
            ) : null}
            {Number(st.shortage_deduction) > 0 ? (
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">− Shortage</span>
                <span className="tabular-nums">{moneyInr(st.shortage_deduction)}</span>
              </div>
            ) : null}
            {Number(st.tds_amount) > 0 ? (
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">− TDS</span>
                <span className="tabular-nums">{moneyInr(st.tds_amount)}</span>
              </div>
            ) : null}
          </>
        )}
        {(Number(st.tds_amount) === 0 || st.tds_reason) && !lines.some((l) => l.code === "TDS") ? (
          <div className="flex justify-between gap-2 text-xs pt-0.5">
            <span className="text-muted-foreground min-w-0">
              TDS{st.tds_pct != null ? ` (${st.tds_pct}%)` : ""}
              {st.tds_reason ? (
                <span className="block text-[10px] leading-snug">{st.tds_reason}</span>
              ) : null}
            </span>
            <span className="tabular-nums shrink-0">{moneyInr(st.tds_amount || 0)}</span>
          </div>
        ) : null}
        <div className="flex justify-between gap-2 text-xs border-t border-border/50 pt-1.5 mt-1">
          <span className="font-medium">Net freight payable</span>
          <span className="tabular-nums font-semibold text-primary">{moneyInr(net)}</span>
        </div>
        {st.total_deductions != null ? (
          <p className="text-[10px] text-muted-foreground text-right">
            Total deductions {moneyInr(st.total_deductions)}
          </p>
        ) : null}
      </div>

      {cash.length ? (
        <div className="rounded-md border border-border/50 bg-background/60 p-2 space-y-1">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
            Cash collections (not from freight)
          </p>
          {cash.map((row, i) => (
            <div key={`cash-${row.code || i}`} className="flex justify-between gap-2 text-xs">
              <span className="text-muted-foreground truncate">{row.label || row.code || "Cash"}</span>
              <span className="tabular-nums shrink-0">{moneyInr(row.amount)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {(slip.pan_no || slip.truck_owner_pan || pack.pan_no) ? (
        <p className="text-[10px] text-muted-foreground">
          PAN {slip.pan_no || slip.truck_owner_pan || pack.pan_no}
          {slip.tds_payee_category || pack.tds_payee_category
            ? ` · ${slip.tds_payee_category || pack.tds_payee_category}`
            : ""}
        </p>
      ) : null}
    </div>
  );
}

/** Read-only review for finalized sales (+ freight) vouchers with print/PDF. */
export function FinalizedSalesSettlementReview({ dispatch, onEdit, canEdit = true }) {
  const [editBusy, setEditBusy] = useState(false);
  const [tallyPreviewIds, setTallyPreviewIds] = useState(null);
  if (!dispatch) return null;

  const startEdit = async () => {
    if (!onEdit) return;
    try {
      setEditBusy(true);
      await onEdit();
    } finally {
      setEditBusy(false);
    }
  };

  const snap = dispatch.final_snapshot || {};
  const prov = dispatch.provisional_snapshot || {};
  const totals = {
    billable_qtl: snap.billable_qtl ?? dispatch.sales_voucher?.billable_qtl ?? prov.billable_qtl,
    full_amount: snap.full_amount ?? dispatch.sales_voucher_gross ?? prov.full_amount,
    receivable: snap.receivable ?? dispatch.sales_voucher_net ?? prov.receivable,
    cd_amount: snap.cd_amount ?? 0,
    brokerage_amount: snap.brokerage_amount ?? 0,
    ffa_amount: snap.ffa_amount ?? 0,
    freight_on_invoice: snap.freight_on_invoice ?? 0,
    other_deductions_total: snap.other_deductions_total
      ?? dispatch.other_deductions?.reduce((s, x) => s + Number(x.amount || 0), 0)
      ?? 0,
    other_additions_total: snap.other_additions_total
      ?? dispatch.other_additions?.reduce((s, x) => s + Number(x.amount || 0), 0)
      ?? 0,
  };
  const allocations = Array.isArray(snap.allocations) && snap.allocations.length
    ? snap.allocations
    : (dispatch.contract_allocations || []);
  const otherDeds = snap.other_deductions || dispatch.other_deductions || [];
  const otherAdds = snap.other_additions || dispatch.other_additions || [];
  const docs = dispatch.document_details || {};
  const pack = dispatch.freight_pack || {};
  const freight = dispatch.freight_voucher;
  const freightStatus = String(dispatch.freight_status || freight?.deductions?.freight_status || "").toUpperCase();
  const showFreight = Boolean(dispatch.freight_voucher_id) && freightStatus !== "WAIVED";
  const settlementDocs = dispatch.settlement_docs || [];

  return (
    <div className="space-y-4 pt-3 border-t border-border/60" data-testid="finalized-sales-settlement-review">
      <SalesWorkspaceHeader
        dispatch={dispatch}
        voucherMode="FINAL"
        canEdit={false}
        onPreviewTally={() => openSalesTallyPreview(dispatch, setTallyPreviewIds)}
      />
      <SalesStageBanners dispatch={dispatch} />
      <SalesAdjustmentBreakdown breakdown={dispatch.adjustment_breakdown} />
      <div className="flex flex-wrap gap-2">
        <PostSettlementJournalButton dispatch={dispatch} />
      </div>
      <TallyPreviewDialog
        open={Boolean(tallyPreviewIds?.length)}
        onOpenChange={(open) => { if (!open) setTallyPreviewIds(null); }}
        voucherIds={tallyPreviewIds || []}
      />
      <CommercialWeightBar
        millQtl={dispatch.net_weight_qtl}
        partyQtl={allocations[0]?.party_weight_qtl}
        billableQtl={totals.billable_qtl}
        bags={allocations[0]?.bags || dispatch.bags_loaded}
        deliveryCondition={dispatch.delivery_condition}
      />
      <SalesVoucherSummaryRibbon
        totals={{
          full_amount: totals.full_amount,
          freight_on_invoice: totals.freight_on_invoice,
          cd_amount: totals.cd_amount,
          brokerage_amount: totals.brokerage_amount,
          ffa_amount: totals.ffa_amount,
          other_deductions_total: totals.other_deductions_total,
          other_additions_total: totals.other_additions_total,
          gst_amount: snap.gst_amount,
          invoice_total: totals.receivable,
          receivable: totals.receivable,
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <SettlementStatusBadge status="FINAL" />
            {dispatch.sales_voucher_code ? (
              <span className="font-mono text-sm font-semibold text-foreground">{dispatch.sales_voucher_code}</span>
            ) : null}
            {showFreight && dispatch.freight_voucher_code ? (
              <span className="font-mono text-xs text-muted-foreground">· {dispatch.freight_voucher_code}</span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Buyer receivable {moneyInr(totals.receivable)}
            {snap.at ? ` · Finalized ${formatIndiaDateTime(snap.at)}` : null}
            {snap.by ? ` by ${snap.by}` : dispatch.sales_voucher_by ? ` by ${dispatch.sales_voucher_by}` : null}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3 shrink-0">
          {canEdit && onEdit ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={editBusy}
              onClick={startEdit}
              data-testid="finalized-sales-edit"
            >
              {editBusy ? "Opening…" : "Edit voucher"}
            </Button>
          ) : null}
          {dispatch.sales_voucher_id ? (
            <VoucherDocumentActions
              label="Sales"
              voucherId={dispatch.sales_voucher_id}
              voucherCode={dispatch.sales_voucher_code}
            />
          ) : null}
          {showFreight ? (
            <VoucherDocumentActions
              label="Freight"
              voucherId={dispatch.freight_voucher_id}
              voucherCode={dispatch.freight_voucher_code}
            />
          ) : null}
          <div className="flex flex-col gap-1 shrink-0 min-w-[5.5rem]">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium leading-tight">
              Weighment
            </span>
            <OutwardWeighmentReceiptActions dispatchId={dispatch.id} label={dispatch.code} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs rounded-md border border-border/60 bg-muted/20 p-2.5">
        <DetailCell label="Billable" value={totals.billable_qtl != null ? `${Number(totals.billable_qtl).toFixed(2)} qtl` : "—"} />
        <DetailCell label="Full amount" value={moneyInr(totals.full_amount)} />
        <DetailCell label="CD" value={moneyInr(totals.cd_amount)} />
        <DetailCell label="Brokerage" value={moneyInr(totals.brokerage_amount)} />
        {(Number(totals.ffa_amount) || 0) > 0 ? (
          <DetailCell label="FFA / quality" value={moneyInr(totals.ffa_amount)} />
        ) : null}
        {(Number(totals.freight_on_invoice) || 0) > 0 ? (
          <DetailCell label="Freight on invoice" value={moneyInr(totals.freight_on_invoice)} />
        ) : null}
        {(Number(totals.other_deductions_total) || 0) > 0 ? (
          <DetailCell label="Other deductions" value={moneyInr(totals.other_deductions_total)} />
        ) : null}
        {(Number(totals.other_additions_total) || 0) > 0 ? (
          <DetailCell label="Other additions" value={moneyInr(totals.other_additions_total)} />
        ) : null}
        <DetailCell label="Buyer receivable" value={moneyInr(totals.receivable)} emphasize />
        {dispatch.weight_cut_pct != null ? (
          <DetailCell label="Weight cut %" value={dispatch.weight_cut_pct} />
        ) : null}
        {dispatch.price_cut_pct != null ? (
          <DetailCell
            label={`Price cut %${dispatch.price_cut_basis ? ` (${dispatch.price_cut_basis})` : ""}`}
            value={dispatch.price_cut_pct}
          />
        ) : null}
      </div>

      {allocations.length > 0 ? (
        <div className="rounded-md border border-border/60 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">Contract</th>
                <th className="text-right px-2 py-1.5 font-medium">Bags</th>
                <th className="text-right px-2 py-1.5 font-medium">Billable</th>
                <th className="text-right px-2 py-1.5 font-medium">Rate</th>
                <th className="text-right px-2 py-1.5 font-medium">Full</th>
                <th className="text-right px-2 py-1.5 font-medium">CD</th>
                <th className="text-right px-2 py-1.5 font-medium">Brokerage</th>
                <th className="text-right px-2 py-1.5 font-medium">Receivable</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((ln, idx) => (
                <tr
                  key={`${ln.sales_contract_id || ln.allocation_id || idx}`}
                  className="border-t border-border/50"
                >
                  <td className="px-2 py-1.5 font-mono">{ln.contract_number || ln.sales_contract_id || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{ln.bags ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{ln.billable_qtl ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{ln.effective_rate ?? ln.full_rate ?? ln.rate ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{moneyInr(ln.full_amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{moneyInr(ln.cd_amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{moneyInr(ln.brokerage_amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">{moneyInr(ln.buyer_receivable)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {(otherDeds.length > 0 || otherAdds.length > 0) ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {otherDeds.length > 0 ? (
            <div className="rounded-md border border-border/60 p-2.5 space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Other deductions</p>
              {otherDeds.map((row, i) => (
                <p key={`ded-${i}`} className="flex justify-between gap-2">
                  <span className="truncate">{row.label || row.kind || "Deduction"}</span>
                  <span className="tabular-nums shrink-0">{moneyInr(row.amount)}</span>
                </p>
              ))}
            </div>
          ) : null}
          {otherAdds.length > 0 ? (
            <div className="rounded-md border border-border/60 p-2.5 space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Other additions</p>
              {otherAdds.map((row, i) => (
                <p key={`add-${i}`} className="flex justify-between gap-2">
                  <span className="truncate">{row.label || row.kind || "Addition"}</span>
                  <span className="tabular-nums shrink-0">{moneyInr(row.amount)}</span>
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-md border border-border/60 p-2.5 text-xs space-y-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Documents & notes</p>
        <p>
          Invoice: <strong>{docs.invoice_no || "—"}</strong>
          {" · "}9R: <strong>{docs.form_9r_no || "—"}</strong>
          {" · "}Gate pass: <strong>{docs.gate_pass_no || dispatch.gate_pass_code || "—"}</strong>
        </p>
        {settlementDocs.length ? (
          <p>
            Uploaded: <strong>{settlementDocs.map((d) => d.kind).filter(Boolean).join(", ") || "—"}</strong>
          </p>
        ) : null}
        {dispatch.accounts_note ? (
          <p className="text-muted-foreground">Note: {dispatch.accounts_note}</p>
        ) : null}
      </div>

      {showFreight ? (
        <FinalizedFreightSettlementDetail dispatch={dispatch} pack={pack} freight={freight} />
      ) : dispatch.include_freight === false ? (
        <p className="text-xs text-muted-foreground">No separate freight voucher for this trip.</p>
      ) : null}
    </div>
  );
}

const DEDUCT_MODES = [
  { value: "NONE", label: "No — do not deduct" },
  { value: "CASH", label: "Yes — driver paid us in cash" },
  { value: "FROM_FREIGHT", label: "Yes — deduct from freight" },
];

function DeductModeField({ label, amountKey, modeKey, freight, setFreight }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Input
        type="number"
        step="0.01"
        value={freight[amountKey]}
        onChange={(e) => setFreight((f) => ({ ...f, [amountKey]: e.target.value }))}
        className="mt-1 h-9"
      />
      <select
        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
        value={freight[modeKey] || "NONE"}
        onChange={(e) => setFreight((f) => ({ ...f, [modeKey]: e.target.value }))}
      >
        {DEDUCT_MODES.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function SettlementStatusBadge({ status }) {
  if (!status) return null;
  const final = status === "FINAL";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        final ? "bg-bansal-sage/15 text-primary" : "bg-amber-100 text-amber-900"
      }`}
    >
      {final ? "Final" : "Provisional"}
    </span>
  );
}

function autoLoadDate(pack = {}, dispatch = {}) {
  const fromPack = pack.load_date ? String(pack.load_date).slice(0, 10) : "";
  if (fromPack) return fromPack;
  const raw = dispatch.gross_weighed_at || dispatch.loaded_at || "";
  return raw ? String(raw).slice(0, 10) : "";
}

function emptyFreightForm(pack = {}, dispatch = {}) {
  const commissionMode = pack.commission_settlement_mode
    || (pack.deduct_transporter_commission ? "FROM_FREIGHT" : "NONE");
  const dalaMode = pack.dala_settlement_mode
    || (pack.dala_paid_by_driver ? "CASH" : pack.deduct_dala ? "FROM_FREIGHT" : "NONE");
  const biltyMode = pack.bilty_settlement_mode
    || (pack.bilty_paid ? "CASH" : pack.deduct_bilty ? "FROM_FREIGHT" : "NONE");
  const companyId = pack.transport_company_party_id || pack.transporter_party_id || null;
  const companyName = pack.transport_company_name
    || pack.transport_company_display_name
    || pack.bilty_transporter_name
    || pack.transporter_name
    || pack.transporter_owner_name
    || "";
  const sameAs = pack.transporter_same_as_company != null
    ? Boolean(pack.transporter_same_as_company)
    : !pack.transport_company_party_id
      || String(pack.transport_company_party_id || "") === String(pack.transporter_party_id || "");
  return {
    transport_company_party_id: companyId,
    transport_company_name: companyName,
    transport_company_display_name: companyName,
    transporter_same_as_company: sameAs,
    transporter_name: pack.transporter_name || pack.transporter_owner_name || companyName || "",
    transporter_owner_name: pack.transporter_owner_name || pack.transporter_name || companyName || "",
    transporter_party_id: sameAs ? companyId : (pack.transporter_party_id || null),
    transporter_display_name: pack.transporter_display_name || pack.transporter_name || (sameAs ? companyName : ""),
    vehicle_number: pack.vehicle_number || dispatch.vehicle_number || "",
    freight_rate_per_qtl: pack.freight_rate_per_qtl != null ? String(pack.freight_rate_per_qtl) : "",
    load_date: autoLoadDate(pack, dispatch),
    reached_date: pack.reached_date ? String(pack.reached_date).slice(0, 10) : "",
    unload_date: pack.unload_date ? String(pack.unload_date).slice(0, 10) : "",
    halting_applicable: Boolean(pack.halting_applicable),
    halting_free_days: pack.halting_free_days != null ? String(pack.halting_free_days) : "2",
    halting_rate_per_day: pack.halting_rate_per_day != null ? String(pack.halting_rate_per_day) : "",
    halting_days: pack.halting_days != null ? String(pack.halting_days) : "0",
    halting_charges: pack.halting_charges != null ? String(pack.halting_charges) : "",
    halting_condition: pack.halting_condition || "",
    advance_at_dispatch:
      pack.advance_at_dispatch != null
        ? String(pack.advance_at_dispatch)
        : pack.advance != null
          ? String(pack.advance)
          : "",
    transporter_commission: pack.transporter_commission != null ? String(pack.transporter_commission) : "",
    commission_settlement_mode: commissionMode,
    deduct_transporter_commission: commissionMode === "FROM_FREIGHT",
    dala: pack.dala != null ? String(pack.dala) : "",
    dala_settlement_mode: dalaMode,
    deduct_dala: dalaMode === "FROM_FREIGHT",
    dala_paid_by_driver: dalaMode === "CASH",
    bilty_charges:
      pack.bilty_charges != null
        ? String(pack.bilty_charges)
        : pack.clerk_bilty_charge != null
          ? String(pack.clerk_bilty_charge)
          : "",
    bilty_settlement_mode: biltyMode,
    deduct_bilty: biltyMode === "FROM_FREIGHT",
    bilty_paid: biltyMode === "CASH",
    bilty_number: pack.bilty_number || "",
    shortage_condition: pack.shortage_condition || "",
    shortage_qtl: pack.shortage_qtl != null ? String(pack.shortage_qtl) : "",
    shortage_amount: pack.shortage_amount != null ? String(pack.shortage_amount) : "",
    shortage_allowance_mode: pack.shortage_allowance_mode || "",
    shortage_allowance_value: pack.shortage_allowance_value != null ? String(pack.shortage_allowance_value) : "",
    truck_owner_pan: pack.truck_owner_pan || pack.pan_no || "",
    pan_no: pack.pan_no || pack.truck_owner_pan || "",
    tds_payee_category: pack.tds_payee_category || "",
    tds_small_transporter_exempt: Boolean(pack.tds_small_transporter_exempt),
    tds_manual_override: Boolean(pack.tds_manual_override),
    tds_pct: pack.tds_pct != null && pack.tds_manual_override ? String(pack.tds_pct) : "",
    bank_name: pack.bank_name || "",
    bank_account_no: pack.bank_account_no || "",
    bank_ifsc: pack.bank_ifsc || "",
    from_place: pack.from_place || "",
    to_place: pack.to_place || "",
  };
}

function freightPayloadFromForm(form) {
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const halt = computeHaltingSettlement({
    halting_applicable: form.halting_applicable,
    load_date: form.load_date,
    unload_date: form.unload_date,
    reached_date: form.reached_date,
    halting_free_days: form.halting_free_days,
    halting_rate_per_day: form.halting_rate_per_day,
    halting_charges: form.halting_charges,
    halting_days: form.halting_days,
  });
  const commissionMode = form.commission_settlement_mode || "NONE";
  const dalaMode = form.dala_settlement_mode || "NONE";
  const biltyMode = form.bilty_settlement_mode || "NONE";
  const sameAs = Boolean(form.transporter_same_as_company);
  const companyName = (form.transport_company_name || form.transport_company_display_name || "").trim();
  return {
    transport_company_party_id: form.transport_company_party_id || undefined,
    transport_company_name: companyName || undefined,
    transport_company_display_name: companyName || undefined,
    transporter_same_as_company: sameAs,
    transporter_name: sameAs ? (companyName || undefined) : (form.transporter_name.trim() || undefined),
    transporter_owner_name: sameAs
      ? (companyName || undefined)
      : ((form.transporter_owner_name || form.transporter_name).trim() || undefined),
    transporter_party_id: sameAs
      ? (form.transport_company_party_id || undefined)
      : (form.transporter_party_id || undefined),
    transporter_display_name: sameAs
      ? (companyName || undefined)
      : (form.transporter_display_name || form.transporter_name || undefined),
    vehicle_number: form.vehicle_number.trim() || undefined,
    freight_rate_per_qtl: num(form.freight_rate_per_qtl),
    load_date: form.load_date || undefined,
    reached_date: form.reached_date || undefined,
    unload_date: form.unload_date || undefined,
    halting_applicable: Boolean(form.halting_applicable),
    halting_free_days: form.halting_applicable ? num(form.halting_free_days) : 0,
    halting_rate_per_day: form.halting_applicable ? num(form.halting_rate_per_day) : 0,
    halting_days: halt.halting_days,
    halting_charges: form.halting_applicable ? halt.halting_charges : num(form.halting_charges) || 0,
    halting_condition: form.halting_condition.trim() || undefined,
    advance_at_dispatch: num(form.advance_at_dispatch),
    advance: num(form.advance_at_dispatch),
    transporter_commission: num(form.transporter_commission),
    commission_settlement_mode: commissionMode,
    deduct_transporter_commission: commissionMode === "FROM_FREIGHT",
    dala: num(form.dala),
    dala_settlement_mode: dalaMode,
    dala_paid_by_driver: dalaMode === "CASH",
    deduct_dala: dalaMode === "FROM_FREIGHT",
    bilty_charges: num(form.bilty_charges),
    clerk_bilty_charge: num(form.bilty_charges),
    bilty_settlement_mode: biltyMode,
    bilty_paid: biltyMode === "CASH",
    deduct_bilty: biltyMode === "FROM_FREIGHT",
    bilty_number: form.bilty_number.trim() || undefined,
    bilty_transporter_name: companyName || form.transporter_name.trim() || undefined,
    shortage_condition: form.shortage_condition.trim() || undefined,
    shortage_qtl: num(form.shortage_qtl),
    shortage_amount: num(form.shortage_amount),
    shortage_allowance_mode: form.shortage_allowance_mode || undefined,
    shortage_allowance_value: num(form.shortage_allowance_value),
    truck_owner_pan: (form.truck_owner_pan || form.pan_no || "").trim() || undefined,
    pan_no: (form.pan_no || form.truck_owner_pan || "").trim() || undefined,
    tds_payee_category: form.tds_payee_category || undefined,
    tds_small_transporter_exempt: Boolean(form.tds_small_transporter_exempt),
    tds_manual_override: Boolean(form.tds_manual_override),
    tds_pct: form.tds_manual_override ? num(form.tds_pct) : undefined,
    bank_name: form.bank_name.trim() || undefined,
    bank_account_no: form.bank_account_no.trim() || undefined,
    bank_ifsc: form.bank_ifsc.trim() || undefined,
    from_place: form.from_place.trim() || undefined,
    to_place: form.to_place.trim() || undefined,
  };
}

function FreightHaltingFields({ freight, setFreight, stage = "provisional" }) {
  const halt = computeHaltingSettlement({
    halting_applicable: freight.halting_applicable,
    load_date: freight.load_date,
    unload_date: freight.unload_date,
    reached_date: freight.reached_date,
    halting_free_days: freight.halting_free_days,
    halting_rate_per_day: freight.halting_rate_per_day,
    halting_charges: freight.halting_charges,
    halting_days: freight.halting_days,
  });
  const patch = (partial) => setFreight((f) => applyHaltingPatch(f, partial));
  const isFinal = stage === "final";

  return (
    <div className="col-span-2 sm:col-span-3 rounded-md border border-border/70 bg-muted/20 p-3 space-y-2">
      <label className="flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          checked={Boolean(freight.halting_applicable)}
          onChange={(e) => patch({ halting_applicable: e.target.checked })}
        />
        Halting applicable
      </label>
      {freight.halting_applicable ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Load date</Label>
              <Input type="date" value={freight.load_date} readOnly className="mt-1 h-9 bg-muted/40" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Auto from weigh / load</p>
            </div>
            {isFinal ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reached date</Label>
                  <Input type="date" value={freight.reached_date} onChange={(e) => patch({ reached_date: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Unload date</Label>
                  <Input type="date" value={freight.unload_date} onChange={(e) => patch({ unload_date: e.target.value })} className="mt-1 h-9" />
                </div>
              </>
            ) : (
              <div className="sm:col-span-2 flex items-end">
                <p className="text-[11px] text-muted-foreground pb-2">
                  Reached & unload dates are entered at final settlement (after goods receipt).
                </p>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Free days</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={freight.halting_free_days}
                onChange={(e) => patch({ halting_free_days: e.target.value })}
                className="mt-1 h-9"
                placeholder="e.g. 2"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">From reached date (incl.)</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">₹ / day</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={freight.halting_rate_per_day}
                onChange={(e) => patch({ halting_rate_per_day: e.target.value })}
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Halting ₹</Label>
              <Input type="number" step="0.01" value={freight.halting_charges} readOnly className="mt-1 h-9 bg-muted/40" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {isFinal && freight.reached_date && freight.unload_date ? (
              <>
                {halt.halting_elapsed_days} day(s) at destination (reached→unload) − {halt.halting_free_days} free ={" "}
                <strong>{halt.halting_days} chargeable day(s)</strong>
                {halt.halting_rate_per_day
                  ? <> · ₹{halt.halting_rate_per_day}/day = <strong>₹{halt.halting_charges}</strong></>
                  : null}
              </>
            ) : (
              <>
                Halting ₹ finalizes when reached & unload dates are entered.
                Free days count from reached date (e.g. free 2 = reached day + next day free).
              </>
            )}
          </p>
        </>
      ) : null}
    </div>
  );
}

const PROVISIONAL_STEPS = [
  { id: 1, label: "Contract linkage", hint: "Correct if needed" },
  { id: 2, label: "Provisional review", hint: "Amounts before issue" },
  { id: 3, label: "Freight", hint: "Rate · advances · cuts" },
  { id: 4, label: "Document details", hint: "Invoice · 9R · gate · bilty" },
  { id: 5, label: "Confirm provisional", hint: "At truck dispatch" },
];

const PROVISIONAL_EDIT_STEPS = [
  { id: 1, label: "Contract linkage", hint: "Correct if needed" },
  { id: 2, label: "Provisional review", hint: "Update amounts" },
  { id: 3, label: "Freight", hint: "Rate · advances · cuts" },
  { id: 4, label: "Document details", hint: "Invoice · 9R · gate · bilty" },
  { id: 5, label: "Save provisional", hint: "Update existing SV" },
];

/**
 * Step-wise provisional voucher wizard — issued at truck dispatch.
 * When the SV is already PROVISIONAL, runs in edit mode and PATCHes settlement-draft
 * (does not create a second voucher).
 */
export function ProvisionalSettlementWizard({ dispatch, contracts = [], onSaved, forceEdit = false }) {
  const allocs = dispatch.contract_allocations?.length
    ? dispatch.contract_allocations
    : [{
        sales_contract_id: dispatch.sales_contract_id,
        contract_number: dispatch.sales_contract_number,
        rate: dispatch.contract_rate,
        pricing_mode: dispatch.pricing_mode,
        delivery_condition: dispatch.delivery_condition,
      }];
  const isFor =
    (dispatch.delivery_condition || "").toUpperCase() === "FOR"
    || allocs.some((a) => (a.delivery_condition || "").toUpperCase() === "FOR");
  const isEditMode = Boolean(
    forceEdit
    || (
      dispatch.sales_voucher_id
      && String(dispatch.sales_settlement_status || "").toUpperCase() === "PROVISIONAL"
    ),
  );

  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(() => new Set(isEditMode ? [1, 2, 3, 4] : []));
  const [busy, setBusy] = useState(false);
  const [selectedContracts, setSelectedContracts] = useState(() =>
    (allocs.map((a) => String(a.sales_contract_id)).filter(Boolean)),
  );
  const [rate, setRate] = useState(() => {
    // Oil-point: always show contract ₹/oil-pt — never stale voucher ₹/qtl (e.g. 38814 typo).
    const oilPoint = allocs.some(
      (a) => (a.pricing_mode || dispatch.pricing_mode || "").toUpperCase() === "PER_OIL_POINT",
    );
    const fromVoucher = oilPoint
      ? (dispatch.contract_rate ?? allocs[0]?.rate)
      : (dispatch.sales_voucher?.rate_per_qtl ?? dispatch.contract_rate);
    return fromVoucher != null && fromVoucher !== "" ? String(fromVoucher) : "";
  });
  const [note, setNote] = useState(dispatch.accounts_note || "");
  const [lines, setLines] = useState(() =>
    allocs.map((a) => ({
      sales_contract_id: a.sales_contract_id,
      allocation_id: a.id,
      contract_number: a.contract_number || "",
      oil_pct: a.oil_pct != null ? String(a.oil_pct) : "",
      ffa_pct: a.ffa_pct != null ? String(a.ffa_pct) : "",
      party_weight_qtl: a.party_weight_qtl != null ? String(a.party_weight_qtl) : "",
      bags:
        a.bags != null
          ? String(a.bags)
          : (allocs.length === 1 && dispatch.bags_loaded != null ? String(dispatch.bags_loaded) : ""),
    })),
  );
  const [docs, setDocs] = useState({
    invoice_no: dispatch.document_details?.invoice_no || "",
    form_9r_no: dispatch.document_details?.form_9r_no || "",
    gate_pass_no: dispatch.document_details?.gate_pass_no || dispatch.gate_pass_code || "",
  });
  const [includeFreight, setIncludeFreight] = useState(Boolean(dispatch.include_freight ?? isFor));
  const [freight, setFreight] = useState(() => emptyFreightForm(dispatch.freight_pack || {}, dispatch));
  const [settlementOpts, setSettlementOpts] = useState(() => ({
    use_rate_after_deductions: Boolean(dispatch.settlement_options?.use_rate_after_deductions),
    use_weight_after_bardana: dispatch.settlement_options?.use_weight_after_bardana !== false,
    charge_freight_in_invoice: Boolean(dispatch.settlement_options?.charge_freight_in_invoice),
  }));
  const [commercial, setCommercial] = useState(() =>
    seedCommercialForm(
      dispatch,
      {},
      (contracts || []).find((c) => String(c.id) === String(dispatch.sales_contract_id)) || {},
    ),
  );
  const patchCommercial = (patch) => setCommercial((prev) => ({ ...prev, ...patch }));
  const [tallyPreviewIds, setTallyPreviewIds] = useState(null);
  const [preview, setPreview] = useState(null);
  const [issued, setIssued] = useState(null);
  const [settlementDocs, setSettlementDocs] = useState(() => dispatch.settlement_docs || []);

  const matchingContracts = useMemo(() => {
    const loaded = new Set(
      (dispatch.loaded_item_ids || [])
        .map(Number)
        .filter(Boolean),
    );
    if (!loaded.size && dispatch.item_id) loaded.add(Number(dispatch.item_id));
    if (!loaded.size && Array.isArray(dispatch.batch_sources)) {
      dispatch.batch_sources.forEach((s) => {
        if (s?.item_id) loaded.add(Number(s.item_id));
      });
    }
    if (!loaded.size) return contracts;
    return (contracts || []).filter((c) => c.item_id != null && loaded.has(Number(c.item_id)));
  }, [contracts, dispatch.loaded_item_ids, dispatch.item_id, dispatch.batch_sources]);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const buildAllocations = () =>
    lines.map((row) => {
      const payload = { sales_contract_id: Number(row.sales_contract_id) };
      if (row.allocation_id) payload.allocation_id = Number(row.allocation_id);
      if (row.oil_pct !== "") payload.oil_pct = Number(row.oil_pct);
      if (row.ffa_pct !== "") payload.ffa_pct = Number(row.ffa_pct);
      if (row.party_weight_qtl !== "") payload.party_weight_qtl = Number(row.party_weight_qtl);
      if (row.bags !== "") payload.bags = Number(row.bags);
      return payload;
    });

  const documentPayload = () => ({
    invoice_no: docs.invoice_no.trim() || undefined,
    form_9r_no: docs.form_9r_no.trim() || undefined,
    gate_pass_no: docs.gate_pass_no.trim() || undefined,
    bilty_transporter_name: (freight.transport_company_name || freight.transporter_name || "").trim() || undefined,
    bilty_number: freight.bilty_number.trim() || undefined,
  });

  const settlementOptionsPayload = () => ({
    ...commercialPayload(commercial),
    use_rate_after_deductions: Boolean(settlementOpts.use_rate_after_deductions || commercial.use_rate_after_deductions),
    use_weight_after_bardana: Boolean(settlementOpts.use_weight_after_bardana),
    charge_freight_in_invoice: !isFor && Boolean(settlementOpts.charge_freight_in_invoice || commercial.charge_freight_in_invoice),
  });

  const markDone = (id) => setCompleted((prev) => new Set([...prev, id]));

  const syncLinesToSelection = (ids) => {
    setLines((prev) => {
      const byId = new Map(prev.map((row) => [String(row.sales_contract_id), row]));
      return ids.map((id) => {
        const existing = byId.get(String(id));
        if (existing) return existing;
        const c = contracts.find((x) => String(x.id) === String(id));
        return {
          sales_contract_id: Number(id),
          allocation_id: undefined,
          contract_number: c?.contract_number || "",
          oil_pct: "",
          ffa_pct: "",
          party_weight_qtl: "",
          bags: ids.length === 1 && dispatch.bags_loaded != null ? String(dispatch.bags_loaded) : "",
        };
      });
    });
  };

  const saveContractCorrection = async ({ silent = false } = {}) => {
    if (!selectedContracts.length) {
      toast.error("Select at least one sales contract");
      return false;
    }
    const activeLines = selectedContracts.map((id) => {
      const row = lines.find((l) => String(l.sales_contract_id) === String(id));
      return row || { sales_contract_id: Number(id), bags: "" };
    });
    for (const row of activeLines) {
      if (row.bags === "" || Number(row.bags) <= 0) {
        const c = contracts.find((x) => String(x.id) === String(row.sales_contract_id));
        toast.error(`Enter bags for contract ${c?.contract_number || row.sales_contract_id}`);
        return false;
      }
    }
    const bagSum = activeLines.reduce((s, r) => s + Number(r.bags || 0), 0);
    const truckBags = Number(dispatch.bags_loaded || 0);
    if (truckBags > 0 && bagSum !== truckBags) {
      toast.error(`Contract bags (${bagSum}) must equal truck bags loaded (${truckBags})`);
      return false;
    }
    try {
      if (!silent) setBusy(true);
      const allocations = activeLines.map((row) => ({
        sales_contract_id: Number(row.sales_contract_id),
        bags: Number(row.bags),
        ...(row.party_weight_qtl !== "" && row.party_weight_qtl != null
          ? { planned_qtl: Number(row.party_weight_qtl) }
          : {}),
      }));
      const { data } = await api.post(`/outward-dispatches/${dispatch.id}/link-sales`, {
        sales_contract_id: Number(selectedContracts[0]),
        allow_correction: true,
        allocations,
      });
      if (!silent) toast.success("Contract linkage saved");
      if (data?.contract_allocations?.length) {
        setLines(
          data.contract_allocations.map((a) => ({
            sales_contract_id: a.sales_contract_id,
            allocation_id: a.id,
            contract_number: a.contract_number || "",
            oil_pct: a.oil_pct != null ? String(a.oil_pct) : "",
            ffa_pct: a.ffa_pct != null ? String(a.ffa_pct) : "",
            party_weight_qtl: a.party_weight_qtl != null ? String(a.party_weight_qtl) : "",
            bags: a.bags != null ? String(a.bags) : "",
          })),
        );
        setSelectedContracts(data.contract_allocations.map((a) => String(a.sales_contract_id)));
      }
      // Do not refresh parent here — keeps wizard step state intact
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      if (!silent) setBusy(false);
    }
  };

  const loadPreview = async () => {
    try {
      setBusy(true);
      const oilPoint = allocs.some(
        (a) => (a.pricing_mode || dispatch.pricing_mode || "").toUpperCase() === "PER_OIL_POINT",
      );
      const { data } = await api.post(`/outward-dispatches/${dispatch.id}/settlement-preview`, {
        // Oil-point uses contract ₹/oil-pt — do not send voucher ₹/qtl as rate override.
        rate_per_qtl: oilPoint ? undefined : (rate ? Number(rate) : undefined),
        allocations: buildAllocations(),
        freight_slip: freightPayloadFromForm(freight),
        include_freight: isFor ? true : includeFreight,
        settlement_options: settlementOptionsPayload(),
      });
      setPreview(data);
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirmProvisional = async () => {
    try {
      setBusy(true);
      const oilPoint = allocs.some(
        (a) => (a.pricing_mode || dispatch.pricing_mode || "").toUpperCase() === "PER_OIL_POINT",
      );
      const body = {
        rate_per_qtl: oilPoint ? undefined : (rate ? Number(rate) : undefined),
        accounts_note: note.trim() || undefined,
        allocations: buildAllocations(),
        freight_slip: freightPayloadFromForm(freight),
        include_freight: isFor ? true : includeFreight,
        document_details: documentPayload(),
        settlement_options: settlementOptionsPayload(),
      };
      if (isEditMode) {
        // Update existing provisional SV / freight — do not issue a second voucher.
        const okLink = await saveContractCorrection({ silent: true });
        if (!okLink) return;
        const { data } = await api.patch(
          `/outward-dispatches/${dispatch.id}/settlement-draft`,
          {
            ...body,
            weight_cut_pct: dispatch.weight_cut_pct != null ? Number(dispatch.weight_cut_pct) : 0,
            price_cut_pct: dispatch.price_cut_pct != null ? Number(dispatch.price_cut_pct) : 0,
            price_cut_basis: dispatch.price_cut_basis || "GROSS_PCT",
          },
        );
        toast.success(`Provisional ${data.voucher?.code || dispatch.sales_voucher_code} updated`);
        onSaved?.(data.dispatch || data);
        return;
      }
      const { data } = await api.post(`/outward-dispatches/${dispatch.id}/provisional-confirm`, body);
      setIssued(data);
      toast.success(`Provisional ${data.voucher?.code} issued`);
      onSaved?.(data.dispatch || data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      const ok = await saveContractCorrection();
      if (!ok) return;
      markDone(1);
      setStep(2);
      await loadPreview();
      return;
    }
    if (step === 2) {
      const ok = await loadPreview();
      if (!ok) return;
      markDone(2);
      setStep(3);
      return;
    }
    if (step === 3) {
      markDone(3);
      setStep(4);
      return;
    }
    if (step === 4) {
      markDone(4);
      setStep(5);
      await loadPreview();
      return;
    }
    if (step === 5) {
      await confirmProvisional();
    }
  };

  const toggleContract = (id) => {
    setSelectedContracts((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      syncLinesToSelection(next);
      return next;
    });
  };

  if (issued) {
    return (
      <div className="space-y-3 pt-2 border-t border-border/60">
        <p className="text-sm link-brand font-medium flex items-center gap-2">
          Provisional voucher <span className="font-mono">{issued.voucher?.code}</span>
          <SettlementStatusBadge status="PROVISIONAL" />
        </p>
        <p className="text-xs text-muted-foreground">
          Receivable ₹{(issued.voucher?.net_payable ?? 0).toLocaleString("en-IN")}
          {issued.freight_voucher ? (
            <> · Freight {issued.freight_voucher.code} ₹{(issued.freight_voucher.net_payable ?? 0).toLocaleString("en-IN")}</>
          ) : null}
          {" · "}Moved to Provisional tab for final settlement after goods receipt / lab.
        </p>
        <Button type="button" size="sm" variant="outline" onClick={() => onSaved?.()}>
          Done
        </Button>
      </div>
    );
  }

  const bagSum = selectedContracts.reduce((s, id) => {
    const row = lines.find((l) => String(l.sales_contract_id) === String(id));
    return s + Number(row?.bags || 0);
  }, 0);

  return (
    <div className="space-y-4 pt-2 border-t border-border/60" data-testid="provisional-settlement-wizard">
      <SalesWorkspaceHeader
        dispatch={dispatch}
        voucherMode="PROVISIONAL"
        canEdit
        saving={busy}
        onSaveDraft={async () => {
          const ok = await saveContractCorrection({ silent: true });
          if (ok) toast.success("Draft saved");
        }}
        onPreviewTally={() => openSalesTallyPreview(dispatch, setTallyPreviewIds)}
        onFinalize={goNext}
        finalizeLabel={isEditMode ? "Save provisional updates" : "Issue provisional"}
      />
      <SalesStageBanners dispatch={dispatch} />
      <CommercialWeightBar
        millQtl={dispatch.net_weight_qtl}
        partyQtl={lines[0]?.party_weight_qtl}
        billableQtl={preview?.totals?.billable_qtl}
        bags={lines[0]?.bags || dispatch.bags_loaded}
        deliveryCondition={isFor ? "FOR" : dispatch.delivery_condition}
        partyEditable={isFor}
        partyValue={lines[0]?.party_weight_qtl ?? ""}
        onPartyChange={(v) => updateLine(0, { party_weight_qtl: v })}
      />
      <SalesVoucherSummaryRibbon totals={preview?.totals || { receivable: dispatch.sales_voucher_net }} />
      <TallyPreviewDialog
        open={Boolean(tallyPreviewIds?.length)}
        onOpenChange={(open) => { if (!open) setTallyPreviewIds(null); }}
        voucherIds={tallyPreviewIds || []}
      />
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
          isEditMode ? "bg-sky-100 text-sky-900" : "bg-amber-100 text-amber-900"
        }`}
        >
          {isEditMode ? "Edit at-dispatch" : "At dispatch"}
        </span>
        {isEditMode && dispatch.sales_voucher_code ? (
          <span className="font-mono text-foreground">{dispatch.sales_voucher_code}</span>
        ) : null}
        <span>
          WB net <strong className="text-foreground">{formatWeightKg(dispatch.net_weight_qtl) ?? "—"}</strong>
          {" · "}Truck bags <strong className="text-foreground">{dispatch.bags_loaded ?? "—"}</strong>
          {selectedContracts.length > 0 ? (
            <>
              {" · "}Linked bags <strong className={bagSum === Number(dispatch.bags_loaded || 0) ? "text-foreground" : "text-amber-700"}>{bagSum}</strong>
            </>
          ) : null}
        </span>
      </div>

      <SettlementStepper
        steps={isEditMode ? PROVISIONAL_EDIT_STEPS : PROVISIONAL_STEPS}
        currentStep={step}
        completed={completed}
        onSelect={setStep}
        testId="sales-provisional-stepper"
      />

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Link contracts and enter <strong>bags per contract</strong> (must sum to truck bags loaded).
            Only contracts matching <strong>loaded items</strong> are shown.
          </p>
          <div className="max-h-72 overflow-y-auto rounded-md border border-border/70 divide-y">
            {matchingContracts.map((c) => {
              const id = String(c.id);
              const checked = selectedContracts.includes(id);
              const lineIdx = lines.findIndex((l) => String(l.sales_contract_id) === id);
              const line = lineIdx >= 0 ? lines[lineIdx] : null;
              return (
                <div key={id} className={`px-3 py-2.5 ${checked ? "bg-muted/30" : ""}`}>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleContract(id)} />
                    <span className="min-w-0 flex-1">
                      <span className="font-mono text-xs">{c.contract_number}</span>
                      {" · "}{c.buyer_name}
                      {" · "}{c.variety || c.product_family}
                    </span>
                  </label>
                  {checked && line ? (
                    <div className="mt-2 ml-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bags (this contract)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={line.bags}
                          onChange={(e) => updateLine(lineIdx, { bags: e.target.value })}
                          className="mt-1 h-9"
                          placeholder="Bags"
                        />
                      </div>
                      {isFor || (c.delivery_condition || "").toUpperCase() === "FOR" ? (
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Party wt (qtl)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={line.party_weight_qtl}
                            onChange={(e) => updateLine(lineIdx, { party_weight_qtl: e.target.value })}
                            className="mt-1 h-9"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Billable = min(our net, party wt)
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {dispatch.bags_loaded != null ? (
            <p className={`text-xs ${bagSum === Number(dispatch.bags_loaded) ? "text-muted-foreground" : "text-amber-700"}`}>
              Linked bags {bagSum} / truck {dispatch.bags_loaded}
              {bagSum !== Number(dispatch.bags_loaded) ? " — must match before Next" : ""}
            </p>
          ) : null}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Invoice no.</Label>
              <Input value={docs.invoice_no} onChange={(e) => setDocs((d) => ({ ...d, invoice_no: e.target.value }))} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Form 9R</Label>
              <Input value={docs.form_9r_no} onChange={(e) => setDocs((d) => ({ ...d, form_9r_no: e.target.value }))} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Gate pass no.</Label>
              <Input value={docs.gate_pass_no} onChange={(e) => setDocs((d) => ({ ...d, gate_pass_no: e.target.value }))} className="mt-1 h-9" />
            </div>
            <p className="sm:col-span-2 text-[11px] text-muted-foreground">
              Bilty no. & transporter are set on the Freight step (not repeated here).
            </p>
          </div>
          <div className="rounded-md border border-border/70 p-3 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Truck documents</p>
            <DispatchDocsUpload
              dispatchId={dispatch.id}
              docs={settlementDocs}
              kinds={DISPATCH_DOC_KINDS}
              hint="Upload now if not already done at loading."
              onUploaded={(next) => {
                if (next?.settlement_docs) setSettlementDocs(next.settlement_docs);
              }}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          {lines.map((row, idx) => {
            const a = allocs[idx] || {};
            const needsOil = a.pricing_mode === "PER_OIL_POINT" || dispatch.pricing_mode === "PER_OIL_POINT";
            return (
              <div key={row.allocation_id || idx} className="rounded-lg border border-border/70 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="sm:col-span-4 text-sm font-medium">{a.contract_number || dispatch.sales_contract_number}</div>
                {needsOil ? (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lab oil %</Label>
                      <Input type="number" step="0.01" value={row.oil_pct} onChange={(e) => updateLine(idx, { oil_pct: e.target.value })} className="mt-1 h-9" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lab FFA %</Label>
                      <Input type="number" step="0.01" value={row.ffa_pct} onChange={(e) => updateLine(idx, { ffa_pct: e.target.value })} className="mt-1 h-9" />
                    </div>
                  </>
                ) : (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Rate override</Label>
                    <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1 h-9" />
                  </div>
                )}
              </div>
            );
          })}

          <SettlementReviewTable
            title="Truck commercial terms"
            subtitle="CD, brokerage, bardana and GST from the sales contract — editable before issuing provisional"
            rows={buildSalesTruckReviewRows(preview?.totals || {}, commercial)}
            form={commercial}
            onUpdate={(field, value) => patchCommercial({ [field]: value })}
          />
          <GstFields form={commercial} onPatch={patchCommercial} />
          <div className="rounded-lg border border-border/70 p-3 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Settlement options</p>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={settlementOpts.use_rate_after_deductions}
                onChange={(e) => setSettlementOpts((o) => ({ ...o, use_rate_after_deductions: e.target.checked }))}
              />
              <span>Rate after all deductions (CD, Brokerage) — brokerage also reduces buyer receivable</span>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={settlementOpts.use_weight_after_bardana}
                onChange={(e) => setSettlementOpts((o) => ({ ...o, use_weight_after_bardana: e.target.checked }))}
              />
              <span>Weight after bardana / bag-cut deduction</span>
            </label>
          </div>

          {preview?.totals ? (
            <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
              <p>Billable: <strong>{preview.totals.billable_qtl}</strong> qtl</p>
              <p>Full amount: <strong>₹{(preview.totals.full_amount || 0).toLocaleString("en-IN")}</strong></p>
              <p>Buyer receivable: <strong>₹{(preview.totals.receivable || 0).toLocaleString("en-IN")}</strong></p>
              {(preview.totals.cd_amount || 0) > 0 ? (
                <p>CD: <strong>₹{(preview.totals.cd_amount || 0).toLocaleString("en-IN")}</strong></p>
              ) : null}
              {(preview.totals.brokerage_amount || 0) > 0 ? (
                <p>Brokerage: <strong>₹{(preview.totals.brokerage_amount || 0).toLocaleString("en-IN")}</strong></p>
              ) : null}
              {(preview.settlements || []).some((ln) => ln.pricing_mode === "PER_OIL_POINT") ? (
                <div className="pt-1 mt-1 border-t border-border/50 text-[11px] text-muted-foreground space-y-0.5">
                  {(preview.settlements || [])
                    .filter((ln) => ln.pricing_mode === "PER_OIL_POINT")
                    .map((ln) => (
                      <p key={ln.allocation_id || ln.sales_contract_id}>
                        {ln.contract_number || "Oil-point"}: ₹{ln.full_rate}/oil-pt
                        {" × "}{ln.effective_oil} oil
                        {" × "}{ln.billable_qtl} qtl
                        {" = "}₹{(ln.full_amount || 0).toLocaleString("en-IN")}
                        {ln.lab_oil != null ? ` (lab oil ${ln.lab_oil}%)` : " (provisional at oil base)"}
                      </p>
                    ))}
                </div>
              ) : null}
              {(preview.settlements || []).some((ln) => ln.billable_weight_basis === "MIN_MILL_PARTY") ? (
                <div className="pt-1 mt-1 border-t border-border/50 text-[11px] text-muted-foreground space-y-0.5">
                  {(preview.settlements || [])
                    .filter((ln) => ln.billable_weight_basis === "MIN_MILL_PARTY")
                    .map((ln) => (
                      <p key={`for-${ln.allocation_id || ln.sales_contract_id}`}>
                        FOR billable = min(our {ln.mill_weight_qtl} qtl, party {ln.party_weight_qtl} qtl)
                        {" → "}{ln.net_qtl ?? ln.billable_qtl} qtl
                      </p>
                    ))}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Click Next to compute provisional amounts.</p>
          )}
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={loadPreview}>
            Refresh preview
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          {!isFor ? (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeFreight} onChange={(e) => setIncludeFreight(e.target.checked)} />
              Include freight voucher (pay transporter)
            </label>
          ) : (
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">FOR — freight always included</p>
          )}
          {!isFor ? (
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={settlementOpts.charge_freight_in_invoice}
                onChange={(e) => setSettlementOpts((o) => ({ ...o, charge_freight_in_invoice: e.target.checked }))}
              />
              <span>
                Charge freight in invoice (truck-load) — net WB × freight ₹/qtl
                {settlementOpts.charge_freight_in_invoice && freight.freight_rate_per_qtl ? (
                  <> · est. ₹{((Number(dispatch.net_weight_qtl) || 0) * Number(freight.freight_rate_per_qtl || 0)).toLocaleString("en-IN")}</>
                ) : null}
              </span>
            </label>
          ) : null}
          {(isFor || includeFreight || settlementOpts.charge_freight_in_invoice) ? (
            <div className="space-y-3">
              {(isFor || includeFreight) ? (
                <div className="space-y-3 rounded-md border border-border/70 p-3">
                  <TransporterFreightFields
                    role="company"
                    slip={freight}
                    onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                    vehicleNumber={freight.vehicle_number || dispatch.vehicle_number || ""}
                    ownerName={freight.transport_company_name || freight.transporter_name}
                  />
                  <TransporterSameAsCompanyField
                    slip={freight}
                    onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                  />
                  {!freight.transporter_same_as_company ? (
                    <TransporterFreightFields
                      role="transporter"
                      slip={freight}
                      onSlipChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                      vehicleNumber={freight.vehicle_number || dispatch.vehicle_number || ""}
                      ownerName={freight.transporter_owner_name || freight.transporter_name}
                    />
                  ) : (
                    <p className="text-[11px] text-muted-foreground">
                      Commission (if deducted) posts to the transport company ledger.
                    </p>
                  )}
                </div>
              ) : null}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(isFor || includeFreight) ? (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bilty / LR-RR no.</Label>
                    <Input value={freight.bilty_number} onChange={(e) => setFreight((f) => ({ ...f, bilty_number: e.target.value }))} className="mt-1 h-9" />
                  </div>
                ) : null}
                {(isFor || includeFreight) ? (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Carrier name / agent</Label>
                    <Input
                      value={freight.carrier_name || freight.bilty_transporter_name || freight.transport_company_name || ""}
                      onChange={(e) => setFreight((f) => ({
                        ...f,
                        carrier_name: e.target.value,
                        bilty_transporter_name: e.target.value,
                      }))}
                      className="mt-1 h-9"
                    />
                  </div>
                ) : null}
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Freight ₹/qtl</Label>
                  <Input type="number" step="0.01" value={freight.freight_rate_per_qtl} onChange={(e) => setFreight((f) => ({ ...f, freight_rate_per_qtl: e.target.value }))} className="mt-1 h-9" />
                </div>
                {(isFor || includeFreight) ? (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Advance at dispatch</Label>
                      <Input type="number" step="0.01" value={freight.advance_at_dispatch} onChange={(e) => setFreight((f) => ({ ...f, advance_at_dispatch: e.target.value }))} className="mt-1 h-9" />
                    </div>
                    <FreightHaltingFields freight={freight} setFreight={setFreight} stage="provisional" />
                    <DeductModeField label="Transporter commission ₹" amountKey="transporter_commission" modeKey="commission_settlement_mode" freight={freight} setFreight={setFreight} />
                    <DeductModeField label="Dala ₹" amountKey="dala" modeKey="dala_settlement_mode" freight={freight} setFreight={setFreight} />
                    <DeductModeField label="Bilty charges ₹" amountKey="bilty_charges" modeKey="bilty_settlement_mode" freight={freight} setFreight={setFreight} />
                    <FreightTdsFields
                      className="col-span-2 sm:col-span-3"
                      value={freight}
                      panKey="pan_no"
                      onChange={(next) => setFreight((f) => ({ ...f, ...next }))}
                    />
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Freight voucher skipped for this truck-load dispatch.</p>
          )}
        </div>
      )}

      {step === 5 && (
        <div className="space-y-3">
          <div className="rounded-lg border border-border/70 p-3 text-sm space-y-3">
            <div>
              <p className="font-medium">
                {isEditMode ? "Save provisional voucher updates" : "Confirm provisional voucher"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEditMode
                  ? "Updates the existing provisional sales (and freight) voucher — same voucher number. Final settlement still happens later from this tab."
                  : "Review the full provisional sales voucher before issue. Final settlement (goods receipt / lab) happens later from the Provisional tab."}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Vehicle</p>
                <p className="font-medium">{dispatch.vehicle_number || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Loaded bags</p>
                <p className="font-medium">{dispatch.bags_loaded ?? lines.reduce((s, r) => s + Number(r.bags || 0), 0)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">WB net</p>
                <p className="font-medium">{preview?.totals?.truck_net ?? dispatch.net_weight_qtl ?? "—"} qtl</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Billable / net after bardana</p>
                <p className="font-medium">{preview?.totals?.billable_qtl ?? "—"} qtl</p>
              </div>
            </div>

            {(preview?.settlements || []).length ? (
              <div className="rounded-md border border-border/60 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium">Contract</th>
                      <th className="text-right px-2 py-1.5 font-medium">Bags</th>
                      <th className="text-right px-2 py-1.5 font-medium">Net</th>
                      <th className="text-right px-2 py-1.5 font-medium">Cut</th>
                      <th className="text-right px-2 py-1.5 font-medium">Billable</th>
                      <th className="text-right px-2 py-1.5 font-medium">Rate</th>
                      <th className="text-right px-2 py-1.5 font-medium">CD</th>
                      <th className="text-right px-2 py-1.5 font-medium">Brokerage</th>
                      <th className="text-right px-2 py-1.5 font-medium">Receivable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.settlements || []).map((ln) => (
                      <tr key={`${ln.sales_contract_id}-${ln.allocation_id || 0}`} className="border-t border-border/50">
                        <td className="px-2 py-1.5">{ln.contract_number || ln.sales_contract_id}</td>
                        <td className="px-2 py-1.5 text-right">{ln.bags ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right">{ln.net_qtl ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right">
                          {ln.bag_cut_qtl != null ? ln.bag_cut_qtl : "—"}
                          {ln.bardana_gm ? (
                            <span className="block text-[10px] text-muted-foreground">
                              {ln.bardana_gm}g {ln.bardana_basis === "PER_QTL" ? "/qtl" : "/bag"}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 text-right">{ln.billable_qtl ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right">{ln.effective_rate ?? ln.full_rate ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right">₹{(ln.cd_amount || 0).toLocaleString("en-IN")}</td>
                        <td className="px-2 py-1.5 text-right">₹{(ln.brokerage_amount || 0).toLocaleString("en-IN")}</td>
                        <td className="px-2 py-1.5 text-right font-medium">₹{(ln.buyer_receivable || 0).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {preview?.totals ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs rounded-md bg-muted/40 p-2">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Full amount</p>
                  <p className="font-medium">₹{(preview.totals.full_amount || 0).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CD total</p>
                  <p className="font-medium">₹{(preview.totals.cd_amount || 0).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Brokerage total</p>
                  <p className="font-medium">₹{(preview.totals.brokerage_amount || 0).toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Buyer receivable</p>
                  <p className="font-medium">₹{(preview.totals.receivable || 0).toLocaleString("en-IN")}</p>
                </div>
              </div>
            ) : null}

            <div className="rounded-md border border-border/60 p-2 text-xs space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Documents</p>
              <p>Invoice: <strong>{docs.invoice_no || "—"}</strong> · 9R: <strong>{docs.form_9r_no || "—"}</strong></p>
              <p>Gate pass: <strong>{docs.gate_pass_no || "—"}</strong></p>
              {settlementDocs.length ? (
                <p>
                  Uploaded:{" "}
                  <strong>{settlementDocs.map((d) => d.kind).join(", ")}</strong>
                </p>
              ) : (
                <p className="text-muted-foreground">No truck docs uploaded yet (RC / cheque / Aadhaar).</p>
              )}
            </div>

            <div className="rounded-md border border-border/60 p-2 text-xs space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Freight</p>
              <p>Freight voucher: <strong>{(isFor || includeFreight) ? "Yes" : "No"}</strong>
                {" · "}Freight on invoice: <strong>{!isFor && settlementOpts.charge_freight_in_invoice ? "Yes" : "No"}</strong>
              </p>
              {(isFor || includeFreight || settlementOpts.charge_freight_in_invoice) ? (
                <>
                  <p>
                    Company:{" "}
                    <strong>{freight.transport_company_name || freight.transporter_name || "—"}</strong>
                    {" · "}Transporter:{" "}
                    <strong>
                      {freight.transporter_same_as_company
                        ? (freight.transport_company_name || freight.transporter_name || "—")
                        : (freight.transporter_name || freight.transporter_owner_name || "—")}
                    </strong>
                    {" · "}Bilty: <strong>{freight.bilty_number || "—"}</strong>
                  </p>
                  <p>Rate: <strong>₹{freight.freight_rate_per_qtl || "—"}/qtl</strong>
                    {" · "}Advance: <strong>₹{(Number(freight.advance_at_dispatch) || 0).toLocaleString("en-IN")}</strong>
                  </p>
                  {(preview?.totals?.freight_on_invoice || 0) > 0 ? (
                    <p>Freight on invoice: <strong>₹{(preview.totals.freight_on_invoice || 0).toLocaleString("en-IN")}</strong></p>
                  ) : null}
                  {preview?.freight_settlement ? (
                    <>
                      <p>Gross freight: <strong>₹{(preview.freight_settlement.gross_freight || 0).toLocaleString("en-IN")}</strong>
                        {" · "}Net payable: <strong>₹{(preview.freight_settlement.net_freight_payable || 0).toLocaleString("en-IN")}</strong>
                      </p>
                      {(preview.freight_settlement.lines || []).length ? (
                        <ul className="list-disc pl-4 text-muted-foreground">
                          {preview.freight_settlement.lines.map((fl) => (
                            <li key={fl.code || fl.label}>
                              {fl.label}: ₹{(fl.amount || 0).toLocaleString("en-IN")}
                              {fl.effect === "subtract" ? " (−)" : ""}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : (
                <p className="text-muted-foreground">No freight charged on this provisional.</p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Accounts note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" disabled={busy || step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>
          Back
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={goNext}
          className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
        >
          {busy
            ? "Working…"
            : step === 5
              ? (isEditMode ? "Save provisional updates" : "Confirm provisional voucher")
              : "Next"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Final settlement panel for provisional vouchers (goods receipt / lab later).
 * Renders 3-step FinalSettlementWizard with draft save on each step.
 */
export default function OutwardSettlementPanel({ dispatch, onSaved, mode = "final" }) {
  const skipPanel =
    mode === "provisional-issue"
    || dispatch.voucher_bucket === "dispatch"
    || !dispatch.sales_voucher_id;

  if (skipPanel) return null;
  return <FinalSettlementWizard dispatch={dispatch} onSaved={onSaved} />;
}
